import { Monitor, Search } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { catalog, DEFAULT_SEARCH, outputResolutionFilterOptions, paramsFromSearchState } from '../lib/catalog';
import { navigate } from '../lib/navigation';
import { normalizeDeviceName } from '../lib/deviceNames';
import { ConnectionBadge } from './ConnectionBadge';
import { ConnectionIcon } from './ConnectionIcon';
import { DevicePicker } from './DevicePicker';
import { SearchablePicker, type PickerOption } from './SearchablePicker';

const connectionOptions: PickerOption[] = [
  { value: '', label: 'All connections', icon: <ConnectionIcon connection="" /> },
  ...catalog.connections.map((connection) => ({ value: connection, label: connection, icon: <ConnectionIcon connection={connection} />, content: <ConnectionBadge connection={connection} showIcon={false} /> })),
];

const resolutionOptions: PickerOption[] = [
  { value: '', label: 'All resolutions', icon: <Monitor size={22} /> },
  ...outputResolutionFilterOptions.map((resolution) => ({ value: resolution, label: resolution, icon: <Monitor size={22} /> })),
];

interface SearchFormProps {
  initialQuery?: string;
  initialDevice?: string;
  initialConnection?: string;
  initialResolution?: string;
  compact?: boolean;
}

export function SearchForm({
  initialQuery = '',
  initialDevice = '',
  initialConnection = '',
  initialResolution = '',
  compact = false,
}: SearchFormProps) {
  const [query, setQuery] = useState(initialQuery);
  const [device, setDevice] = useState(normalizeDeviceName(initialDevice));
  const [connection, setConnection] = useState(initialConnection);
  const [resolution, setResolution] = useState(initialResolution);

  function submit(event: FormEvent): void {
    event.preventDefault();
    const params = paramsFromSearchState({ ...DEFAULT_SEARCH, query, device, connection, resolution });
    navigate(`/search${params.size ? `?${params}` : ''}`);
  }

  return (
    <form className={compact ? 'search-form compact' : 'search-form'} onSubmit={submit} role="search">
      <label className="search-query">
        <span>Name</span>
        <Search size={19} aria-hidden="true" />
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by profile, creator, pack…" />
      </label>
      <div className="search-field">
        <span className="search-field-label">Console / Device</span>
        <DevicePicker value={device} onChange={setDevice} label="Console / Device" includeInstructions />
      </div>
      <div className="search-field">
        <span className="search-field-label">Connection</span>
        <SearchablePicker value={connection} onChange={setConnection} options={connectionOptions} label="Connection" dialogLabel="Choose connection" searchLabel="Find a connection" />
      </div>
      {!compact && (
        <div className="search-field">
          <span className="search-field-label">Output Resolution</span>
          <SearchablePicker value={resolution} onChange={setResolution} options={resolutionOptions} label="Output Resolution" dialogLabel="Choose output resolution" searchLabel="Find a resolution" />
        </div>
      )}
      <button className="button primary" type="submit"><Search size={17} />Search profiles</button>
    </form>
  );
}
